import type { ScenarioSpec } from "./types";
import { officeUnderAttackScenario as OUA } from "./office-under-attack.scenario";

/**
 * Reception terminal in the NorthBay office, then the counter PC.
 * The film plays on the terminal. The browser game runs on the desk monitor.
 */
const VIDEO = "/cdn/sim/awareness-videos/malware/malware.mp4";
const FILM_POSTER = "/cdn/sim/awareness-videos/malware/poster.jpg";
const GAME_STILL = "/cdn/sim/awareness-videos/malware/wide/01-task.jpg";

export const malwareScenario: ScenarioSpec = {
  version: 1,
  id: "malware",
  title: "Malware: how a game site asks you to run it",
  summary: {
    goal: "Learn how an ordinary website can push you to allow notifications, add a browser extension or run a download, and what to do instead.",
    youDo: "Watch a short film, browse a news site on the counter computer and decide what to allow, install or run, then answer four questions.",
    duration: "Core 4-7 min, browser game adds 5-7 min",
    tags: ["malware", "unsafe_downloads", "incident_reporting"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  topic: "An ordinary game page can ask for notifications, an extension, and a repair that is really malware",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: OUA.voiceAttackers,
  scene: OUA.scene,

  devices: [
    {
      id: "malware_terminal",
      kind: "breachbrief",
      config: {
        noticeTag: "MALWARE",
        noticeKicker: "SECURITY AWARENESS",
        title: "Malware",
        offLabel: "Switch on the welcome terminal",
        voiceId: "onyx",
        progressKey: "malware",
        idleImage: GAME_STILL,
        videoFirst: true,
        lines: [
          "A normal work morning. You will use the computer behind this counter.",
          "Watch the film, then play what happens when a news-site game asks you to fix your browser.",
        ],
        hubIntro: "Watch the film, then play the browser game on the counter computer.",
        hubVideo: {
          title: "Malware",
          blurb: "How a familiar page turns a small yes into something that runs on the computer.",
          src: VIDEO,
          poster: FILM_POSTER,
        },
        exercisesKicker: "1 interactive exercise",
        exercisesLabel: "Practise",
        exercises: [
          {
            id: "malware-desk",
            label: "The news-site game",
            blurb: "Open the browser on the counter PC. A game ad, a permission, and a repair that is not a repair.",
            key: "play",
            completionBeatId: "b_game",
            icon: "chapters",
            imageUrl: GAME_STILL,
            actionLabel: "Play on the computer",
            doneLabel: "Done",
          },
        ],
        cards: [],
        finishEarlyHint: "You can take the quiz now, or watch the film and play the game first.",
      },
    },
    {
      id: "malware_pc",
      kind: "malwarepc",
      config: { title: "Counter computer" },
    },
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Malware",
      kicker: "Reception",
      guide: { title: "Start", body: "You start at the unattended front desk of the NorthBay office on a normal work morning. The welcome terminal shows a short film, then sends you to the computer behind the counter, where you open the browser on a news site." },
    },
    { id: "b_cam_terminal", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_hub",
      type: "device",
      deviceId: "malware_terminal",
      guide: {
        title: "Reception terminal",
        body: "Switch on the welcome terminal. The film is on the left. The game is on the counter computer.",
      },
      completeOn: { action: "continue" },
    },
    { id: "b_cam_desk", type: "camera", waypointId: "wp_desk_signin", walkSfx: true },
    {
      id: "b_game",
      type: "device",
      deviceId: "malware_pc",
      guide: {
        title: "Counter computer",
        body: "The work computer is on. Open Chrome when you are ready.",
      },
      completeOn: { action: "continue" },
    },
    { id: "b_cam_return", type: "camera", waypointId: "wp_terminal_close", walkSfx: true },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check what you would do",
      quiz: [
        {
          id: "q_ad",
          title: "Question 1",
          prompt: "You click an IQ-test ad on a news site and a game opens. What has happened so far?",
          options: [
            { id: "a", label: "The computer is already infected.", icon: "x" },
            { id: "b", label: "You opened a website. That click alone did not run malware.", correct: true, icon: "check" },
            { id: "c", label: "The news site stole your files.", icon: "alert" },
          ],
          rationale: "Opening the game starts the story. The risk is what the site asks you to allow or run next.",
        },
        {
          id: "q_notify",
          title: "Question 2",
          prompt: "The game asks to send notifications so you can see your score. What does Allow actually do?",
          options: [
            { id: "a", label: "It gives the site control of the computer.", icon: "x" },
            { id: "b", label: "It lets that site contact you later, even after you leave the page.", correct: true, icon: "check" },
            { id: "c", label: "It installs the game as an app.", icon: "alert" },
          ],
          rationale: "Notification permission is a way back in. It is not full control of the PC. The game still works if you block it.",
        },
        {
          id: "q_ext",
          title: "Question 3",
          prompt: "A game page offers a browser extension that can read and change data on every website you visit. What do you do?",
          options: [
            { id: "a", label: "Add it. It is only a game.", icon: "x" },
            { id: "b", label: "Do not add it. That access covers your work sites too. Install only extensions your company approves.", correct: true, icon: "check" },
            { id: "c", label: "Add it now and remove it later.", icon: "alert" },
          ],
          rationale: "An extension that can read and change every site sees your work accounts too. Looking at the listing is harmless; adding it is the risk.",
        },
        {
          id: "q_repair",
          title: "Question 4",
          prompt: "The game freezes and says to repair the browser, then to run steps in a command tool. What should you do?",
          options: [
            { id: "a", label: "Run the steps. The site says your progress is saved.", icon: "x" },
            { id: "b", label: "Close the page. Do not run a repair from a game site. Tell IT if you already tried.", correct: true, icon: "check" },
            { id: "c", label: "Download a second cleaner from the warning.", icon: "alert" },
          ],
          rationale: "A game site does not fix your browser. The manufactured error is there to get you to run something. Company controls may block it. Still tell IT.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_cam_terminal" },
    { from: "b_cam_terminal", to: "b_hub" },
    { from: "b_hub", to: "b_cam_desk", on: "play" },
    { from: "b_cam_desk", to: "b_game" },
    { from: "b_game", to: "b_cam_return" },
    { from: "b_cam_return", to: "b_hub" },
    { from: "b_hub", to: "b_quiz", on: "finish" },
  ],

  initialBeatId: "b_intro",
};
